import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCurrentDeviceNotificationStatus,
  getCurrentScheduleNotificationStatus,
  getInitialNotificationDeviceStatus,
  getInitialScheduleNotificationStatus,
  NOTIFICATION_DEVICE_STATUS_EVENT,
  SCHEDULE_NOTIFICATION_STATUS_EVENT
} from '../services/notificationService';

export default function useNotificationDeviceStatus(userId, userLevel) {
  const deviceRequestIdRef = useRef(0);
  const scheduleRequestIdRef = useRef(0);
  const [status, setStatus] = useState(() => (
    getInitialNotificationDeviceStatus(userId, userLevel)
  ));
  const [scheduleStatus, setScheduleStatus] = useState(() => (
    getInitialScheduleNotificationStatus(userId, userLevel)
  ));

  const refreshDeviceStatus = useCallback(async () => {
    const requestId = deviceRequestIdRef.current + 1;
    deviceRequestIdRef.current = requestId;
    const nextStatus = await getCurrentDeviceNotificationStatus(userId, userLevel);
    if (deviceRequestIdRef.current === requestId) {
      setStatus(nextStatus);
    }
    return nextStatus;
  }, [userId, userLevel]);

  const refreshScheduleStatus = useCallback(async () => {
    const requestId = scheduleRequestIdRef.current + 1;
    scheduleRequestIdRef.current = requestId;
    const nextStatus = await getCurrentScheduleNotificationStatus(userId, userLevel);
    if (scheduleRequestIdRef.current === requestId) {
      setScheduleStatus(nextStatus);
    }
    return nextStatus;
  }, [userId, userLevel]);

  const refresh = useCallback(() => Promise.all([
    refreshDeviceStatus(),
    refreshScheduleStatus()
  ]), [refreshDeviceStatus, refreshScheduleStatus]);

  useEffect(() => {
    deviceRequestIdRef.current += 1;
    scheduleRequestIdRef.current += 1;
    setStatus(getInitialNotificationDeviceStatus(userId, userLevel));
    setScheduleStatus(getInitialScheduleNotificationStatus(userId, userLevel));

    const handleDeviceStatusChange = (event) => {
      if (Number(event.detail?.userId) !== Number(userId) || !event.detail?.status) {
        return;
      }
      deviceRequestIdRef.current += 1;
      setStatus(event.detail.status);
    };
    const handleScheduleStatusChange = (event) => {
      if (Number(event.detail?.userId) !== Number(userId) || !event.detail?.status) {
        return;
      }
      scheduleRequestIdRef.current += 1;
      setScheduleStatus(event.detail.status);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    const handleReturnToApp = () => refresh();

    window.addEventListener(NOTIFICATION_DEVICE_STATUS_EVENT, handleDeviceStatusChange);
    window.addEventListener(SCHEDULE_NOTIFICATION_STATUS_EVENT, handleScheduleStatusChange);
    window.addEventListener('focus', handleReturnToApp);
    window.addEventListener('pageshow', handleReturnToApp);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    refresh();

    return () => {
      deviceRequestIdRef.current += 1;
      scheduleRequestIdRef.current += 1;
      window.removeEventListener(NOTIFICATION_DEVICE_STATUS_EVENT, handleDeviceStatusChange);
      window.removeEventListener(SCHEDULE_NOTIFICATION_STATUS_EVENT, handleScheduleStatusChange);
      window.removeEventListener('focus', handleReturnToApp);
      window.removeEventListener('pageshow', handleReturnToApp);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refresh, userId, userLevel]);

  return { status, scheduleStatus, refresh };
}
